import * as React from "react";
import { Trash2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FilterCombobox } from "../common/FilterCombobox";
import {
  drawerFilterItemClassName,
  drawerFiltersBoxClassName,
} from "../common/Table/TableStyles";
import JobInfoView from "./JobInfoView";
import JobRequestStatusChip from "./JobRequestStatusChip";
import StopJobButton from "./StopJobButton";
import { canStopJob, isJobFinished } from "../../models/Jobs/JobActions";
import {
  JobRequestStatus,
  JobRequestStatusColors,
  JobRequestStatusIcons,
  JobRequestStatusLabels,
} from "../../models/Jobs/JobRequestStatus";
import { JobRequest } from "../../models/Jobs/JobRequest";

interface DeletionStatusListProps<T extends JobRequest> {
  jobs: T[];
  loading: boolean;
  statusFilter: string | null;
  onStatusFilterChange: (value: string | null) => void;
  onAbort: (job: T) => void;
  /** Called once a stop request has been taken, to refresh the list. */
  onStopped?: () => Promise<unknown> | void;
  emptyMessage?: string;
}

/**
 * Vertical card list of deletion-job requests, designed for the narrow
 * right-side "Manage files" drawer (~384px). Replaces a 3-column table
 * whose whitespace-nowrap cells forced horizontal overflow when entity
 * info, status, and action buttons were all squeezed side-by-side.
 */
export function DeletionStatusList<T extends JobRequest>({
  jobs,
  loading,
  statusFilter,
  onStatusFilterChange,
  onAbort,
  onStopped,
  emptyMessage = "No pending deletions.",
}: DeletionStatusListProps<T>) {
  const filtered = jobs.filter(
    (j) => statusFilter == null || j.status === statusFilter
  );
  const sorted = [...filtered].sort((a, b) =>
    (b.id ?? "").localeCompare(a.id ?? "")
  );

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={drawerFiltersBoxClassName}>
        <div className={drawerFilterItemClassName}>
          <Label className="text-sm">Status</Label>
          <FilterCombobox
            value={statusFilter}
            onChange={onStatusFilterChange}
            placeholder="Status"
            options={Object.values(JobRequestStatus)
              .filter((status) => status !== JobRequestStatus.New)
              .map((status) => ({
                value: status as string,
                label: JobRequestStatusLabels[status as JobRequestStatus],
                color: JobRequestStatusColors[status as JobRequestStatus],
                icon: JobRequestStatusIcons[status as JobRequestStatus],
              }))}
          />
        </div>
      </div>

      <div
        className={cn(
          "flex flex-col flex-1 min-h-0 overflow-y-auto rounded-md border bg-background",
          loading && "opacity-50 pointer-events-none"
        )}
      >
        {sorted.length === 0 ? (
          <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          <ul className="divide-y">
            {sorted.map((job) => {
              const canEdit = job.status === JobRequestStatus.Pending || isJobFinished(job);
              return (
                <li key={job.id} className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <JobInfoView jobRequest={job} />
                    </div>
                    {canStopJob(job) && <StopJobButton job={job} onStopped={onStopped} />}
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={isJobFinished(job) ? "Clear finished job" : "Cancel job"}
                        className="text-destructive shrink-0"
                        onClick={() => onAbort(job)}
                      >
                        {isJobFinished(job) ? (
                          <Trash2 className="size-4" />
                        ) : (
                          <XCircle className="size-4" />
                        )}
                      </Button>
                    )}
                  </div>
                  <JobRequestStatusChip status={job.status} />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default DeletionStatusList;
